import { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Download, Upload, Loader2, Archive, AlertTriangle, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import api from '../utils/api';

export default function DataExport({ user }) {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [mergeMode, setMergeMode] = useState('skip');
  const [importResults, setImportResults] = useState(null);
  const fileInputRef = useRef(null);

  // Only show for main admin
  const isMainAdmin = user?.email === 'muneeb.mahadik@lioncroft.co.uk';

  if (!isMainAdmin) {
    return null;
  }

  const exportAllData = async () => {
    setExporting(true);
    setProgress('Starting export...');

    try {
      const exportData = {
        export_date: new Date().toISOString(),
        source: window.location.origin,
        version: '3.0',
        collections: {}
      };

      setProgress('Exporting users...');
      const usersRes = await api.get('/users');
      exportData.collections.users = usersRes.data.filter(u => !u.is_test_user);

      setProgress('Exporting customers...');
      const customersRes = await api.get('/customers');
      exportData.collections.customers = customersRes.data;

      setProgress('Exporting vehicles...');
      const vehiclesRes = await api.get('/vehicles');
      exportData.collections.vehicles = vehiclesRes.data;

      setProgress('Exporting deliveries...');
      const deliveriesRes = await api.get('/deliveries');
      const deliveries = deliveriesRes.data;
      exportData.collections.deliveries = deliveries;

      setProgress(`Exporting pallets (${deliveries.length} deliveries)...`);
      const allPallets = [];
      for (let i = 0; i < deliveries.length; i++) {
        try {
          const palletsRes = await api.get(`/deliveries/${deliveries[i].id}/pallets`);
          allPallets.push(...palletsRes.data);
          if ((i + 1) % 10 === 0) {
            setProgress(`Exporting pallets (${i + 1}/${deliveries.length})...`);
          }
        } catch (e) {
          console.error(`Failed to get pallets for delivery ${deliveries[i].id}`);
        }
      }
      exportData.collections.pallets = allPallets;

      setProgress('Exporting pallet movements...');
      const allMovements = [];
      const customers = exportData.collections.customers;
      for (let i = 0; i < customers.length; i++) {
        try {
          const movementsRes = await api.get(`/pallet-movements/customer/${customers[i].id}`);
          allMovements.push(...movementsRes.data);
          if ((i + 1) % 5 === 0) {
            setProgress(`Exporting movements (${i + 1}/${customers.length})...`);
          }
        } catch (e) {
          console.error(`Failed to get movements for customer ${customers[i].id}`);
        }
      }
      exportData.collections.pallet_movements = allMovements;

      setProgress('Exporting notifications...');
      const notificationsRes = await api.get('/notifications');
      exportData.collections.notifications = notificationsRes.data;

      setProgress('Creating download file...');
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `workforce_export_${timestamp}.json`;

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const summary = {
        users: exportData.collections.users.length,
        customers: exportData.collections.customers.length,
        vehicles: exportData.collections.vehicles.length,
        deliveries: exportData.collections.deliveries.length,
        pallets: exportData.collections.pallets.length,
        movements: exportData.collections.pallet_movements.length,
        notifications: exportData.collections.notifications.length
      };

      toast.success(`Export complete! Downloaded ${filename}`, {
        description: `${summary.deliveries} deliveries, ${summary.customers} customers, ${summary.pallets} pallets`
      });

      setProgress('');
      setExporting(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Export failed: ' + (error.message || 'Unknown error'));
      setExporting(false);
      setProgress('');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      toast.error('Please select a JSON file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        
        // Validate the file structure
        if (!data.collections) {
          toast.error('Invalid export file: missing collections');
          return;
        }

        setImportFile(data);
        setImportPreview({
          export_date: data.export_date,
          source: data.source,
          version: data.version,
          counts: {
            users: data.collections.users?.length || 0,
            customers: data.collections.customers?.length || 0,
            vehicles: data.collections.vehicles?.length || 0,
            deliveries: data.collections.deliveries?.length || 0,
            pallets: data.collections.pallets?.length || 0,
            pallet_movements: data.collections.pallet_movements?.length || 0,
            notifications: data.collections.notifications?.length || 0
          }
        });
        setImportDialogOpen(true);
      } catch (error) {
        toast.error('Invalid JSON file');
        console.error(error);
      }
    };
    reader.readAsText(file);
    
    // Reset file input
    event.target.value = '';
  };

  const handleImport = async () => {
    if (!importFile) return;

    setImporting(true);
    setProgress('Starting import...');

    try {
      const response = await api.post(`/import-data?merge_mode=${mergeMode}`, importFile);
      const results = response.data;
      
      setImportResults(results);
      
      if (results.success) {
        toast.success('Import completed successfully!');
      } else {
        toast.error('Import completed with errors');
      }
      
      setProgress('');
      setImporting(false);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Import failed: ' + (error.response?.data?.detail || error.message || 'Unknown error'));
      setImporting(false);
      setProgress('');
    }
  };

  const closeImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportPreview(null);
    setImportResults(null);
    setMergeMode('skip');
  };

  return (
    <div className="space-y-4">
      {/* Export Section */}
      <div className="rounded-lg p-6" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
        <div className="flex items-start gap-4">
          <div className="rounded-lg p-3" style={{ background: 'var(--amber)' }}>
            <Download className="h-6 w-6" style={{ color: 'var(--bg-void)' }} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Export Production Data
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Download all your production data including customers, deliveries, pallets, 
              and pallet movements. This will create a complete backup of your data.
            </p>

            {exporting ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--amber)' }}>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{progress}</span>
                </div>
                <div className="w-full rounded-full h-2" style={{ background: 'var(--bg-raised)' }}>
                  <div className="h-2 rounded-full animate-pulse" style={{ width: '60%', background: 'var(--amber)' }}></div>
                </div>
              </div>
            ) : (
              <Button
                onClick={exportAllData}
                className="hover:brightness-110"
                style={{ background: 'var(--amber)', color: 'var(--bg-void)', fontFamily: 'var(--fd)', letterSpacing: '0.05em', textTransform: 'uppercase' }}
                data-testid="export-data-btn"
              >
                <Download className="h-4 w-4 mr-2" />
                Export All Data
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Import Section */}
      <div className="rounded-lg p-6" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
        <div className="flex items-start gap-4">
          <div className="rounded-lg p-3" style={{ background: 'var(--green)' }}>
            <Upload className="h-6 w-6" style={{ color: 'var(--bg-void)' }} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Import Data
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Upload a previously exported JSON file to restore or merge data. 
              You can choose how to handle existing records (skip or replace).
            </p>

            <input
              type="file"
              accept=".json"
              onChange={handleFileSelect}
              ref={fileInputRef}
              className="hidden"
            />
            
            <Button
              onClick={() => fileInputRef.current?.click()}
              className="hover:brightness-110"
              style={{ background: 'var(--green)', color: 'var(--bg-void)', fontFamily: 'var(--fd)', letterSpacing: '0.05em', textTransform: 'uppercase' }}
              data-testid="import-data-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Select JSON File
            </Button>
          </div>
        </div>
      </div>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import Data</DialogTitle>
            <DialogDescription>
              Review the data to be imported and select merge options
            </DialogDescription>
          </DialogHeader>

          {importResults ? (
            // Show results after import
            <div className="space-y-4">
              <div className={`p-4 rounded-lg ${importResults.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {importResults.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  )}
                  <span className={`font-semibold ${importResults.success ? 'text-green-800' : 'text-red-800'}`}>
                    {importResults.success ? 'Import Successful' : 'Import Completed with Errors'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {Object.entries(importResults.imported || {}).map(([collection, count]) => (
                    <div key={collection} className="flex justify-between">
                      <span className="capitalize">{collection.replace('_', ' ')}:</span>
                      <span className="font-medium text-green-700">+{count} imported</span>
                    </div>
                  ))}
                  {Object.entries(importResults.skipped || {}).map(([collection, count]) => (
                    count > 0 && (
                      <div key={`skip-${collection}`} className="flex justify-between">
                        <span className="capitalize">{collection.replace('_', ' ')}:</span>
                        <span className="font-medium text-amber-700">{count} skipped</span>
                      </div>
                    )
                  ))}
                </div>
                
                {importResults.errors && importResults.errors.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-red-200">
                    <p className="text-sm font-medium text-red-800 mb-1">Errors:</p>
                    <ul className="text-xs text-red-700 list-disc ml-4 max-h-24 overflow-y-auto">
                      {importResults.errors.slice(0, 10).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {importResults.errors.length > 10 && (
                        <li>...and {importResults.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button onClick={closeImportDialog}>Done</Button>
              </DialogFooter>
            </div>
          ) : (
            // Show preview before import
            <>
              {importPreview && (
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-2">File Information</h4>
                    <div className="text-sm text-slate-600 space-y-1">
                      <p>Export Date: {importPreview.export_date ? new Date(importPreview.export_date).toLocaleString() : 'N/A'}</p>
                      <p>Source: {importPreview.source || 'N/A'}</p>
                      <p>Version: {importPreview.version || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="bg-slate-50 rounded-lg p-4">
                    <h4 className="font-medium text-slate-900 mb-2">Data to Import</h4>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex justify-between">
                        <span>Users:</span>
                        <span className="font-medium">{importPreview.counts.users}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Customers:</span>
                        <span className="font-medium">{importPreview.counts.customers}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Vehicles:</span>
                        <span className="font-medium">{importPreview.counts.vehicles}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Deliveries:</span>
                        <span className="font-medium">{importPreview.counts.deliveries}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Pallets:</span>
                        <span className="font-medium">{importPreview.counts.pallets}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Movements:</span>
                        <span className="font-medium">{importPreview.counts.pallet_movements}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">
                      How to handle existing records:
                    </label>
                    <Select value={mergeMode} onValueChange={setMergeMode}>
                      <SelectTrigger data-testid="merge-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="skip">Skip existing (keep current data)</SelectItem>
                        <SelectItem value="replace">Replace existing (overwrite with imported data)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {mergeMode === 'skip' 
                        ? 'Only new records will be added. Existing records will not be modified.'
                        : 'Existing records will be overwritten with the imported data.'}
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        <strong>Warning:</strong> This will modify your database. Make sure you have a backup before proceeding.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={closeImportDialog} disabled={importing}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleImport} 
                  disabled={importing}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="confirm-import-btn"
                >
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Start Import
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Instructions */}
      <div className="rounded-lg p-4" style={{ background: 'var(--bg-raised)', border: '1px solid var(--border-color)' }}>
        <h4 className="text-sm font-semibold mb-2" style={{ color: 'var(--amber)' }}>
          Instructions
        </h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--amber)' }}>To Export:</p>
            <ol className="text-sm space-y-1 ml-4 list-decimal" style={{ color: 'var(--text-secondary)' }}>
              <li>Click "Export All Data" button</li>
              <li>Wait for export to complete</li>
              <li>File downloads automatically</li>
            </ol>
          </div>
          <div>
            <p className="text-xs font-medium mb-1" style={{ color: 'var(--amber)' }}>To Import:</p>
            <ol className="text-sm space-y-1 ml-4 list-decimal" style={{ color: 'var(--text-secondary)' }}>
              <li>Click "Select JSON File"</li>
              <li>Choose your export file</li>
              <li>Select merge mode and confirm</li>
            </ol>
          </div>
        </div>
        <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border-color)' }}>
          <Link 
            to="/backups"
            className="text-sm font-medium flex items-center gap-2 hover:underline"
            style={{ color: 'var(--text-secondary)' }}
          >
            <Archive className="h-4 w-4" />
            View Automated Backup Archive
          </Link>
        </div>
      </div>
    </div>
  );
}
